<div class="obs-dashboard">
    <div class="obs-bg">
        <div class="obs-orb obs-orb-1"></div>
        <div class="obs-orb obs-orb-2"></div>
        <div class="obs-orb obs-orb-3"></div>
    </div>

    <!-- Top bar: title left, Back button right (like registered-user toolbar) -->
    <div class="obs-topbar">
        <div class="obs-topbar-left">
            <h1 class="obs-topbar-title">Application observability</h1>
            <p class="obs-topbar-subtitle">Health and resource status · refreshes every 15s</p>
        </div>
        <div class="obs-topbar-right">
            <button type="button" class="obs-topbar-back" (click)="goBack()" title="Back">
                <i class="pi pi-arrow-left"></i>
                Back
            </button>
        </div>
    </div>

    <header class="obs-header">
        <div class="obs-error" *ngIf="error">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ error }}</span>
        </div>
    </header>

    <!-- Section 1: Order Management (current implementation) -->
    <h2 class="obs-section-heading">Order management</h2>

    <div class="obs-grid">
        <!-- Overall health - hero card (one line, compact) -->
        <section class="obs-card obs-card-hero" [class.obs-loading]="loading && !health">
            <div class="obs-card-inner obs-hero-oneline">
                <h2 class="obs-card-title">
                    <i class="pi pi-heart-fill"></i>
                    Overall health
                </h2>
                <ng-container *ngIf="loading && !health">
                    <div class="obs-skeleton obs-skeleton-inline"></div>
                </ng-container>
                <ng-container *ngIf="health">
                    <div class="obs-hero-status obs-hero-status-inline" [class]="'obs-status-' + (health.status || 'UNKNOWN').toLowerCase()">
                        <div class="obs-gauge-ring obs-gauge-status obs-gauge-inline" [style.--value]="health.status === 'UP' ? 100 : 0">
                            <span class="obs-gauge-value">{{ health.status }}</span>
                        </div>
                    </div>
                    <div class="obs-components">
                        <ng-container *ngFor="let row of getComponentEntries()">
                            <span class="obs-pill" [class]="'obs-pill-' + statusSeverity(row[1].status)">{{ row[0] }}: {{ row[1].status }}</span>
                        </ng-container>
                    </div>
                    <button type="button" class="obs-refresh-btn" [disabled]="refreshingOrder" (click)="refreshOrderManagement()" title="Refresh">
                        <i class="pi" [class.pi-refresh]="!refreshingOrder" [class.pi-spin]="refreshingOrder" [class.pi-spinner]="refreshingOrder"></i>
                        {{ refreshingOrder ? 'Refreshing…' : 'Refresh' }}
                    </button>
                </ng-container>
            </div>
        </section>

        <!-- JVM Memory - gauge card -->
        <section class="obs-card obs-card-gauge obs-card-memory">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-database"></i>
                    JVM memory
                </h2>
                <ng-container *ngIf="!memory && !loading">
                    <p class="obs-empty">Memory metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="memory">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-memory" [style.--value]="memoryUtilizationPercent()">
                            <span class="obs-gauge-value">{{ memoryUtilizationPercent() | number:'1.1-1' }}%</span>
                        </div>
                        <p class="obs-gauge-caption">Heap {{ memory.heap.usedMB }} / {{ memory.heap.maxMB }} MB</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(memory.health.status)">{{ memory.health.status }}</span>
                        <span class="obs-hint" *ngIf="memory.health.recommendation">{{ memory.health.recommendation }}</span>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- HikariCP - gauge card -->
        <section class="obs-card obs-card-gauge obs-card-pool">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-server"></i>
                    Connection pool
                </h2>
                <ng-container *ngIf="!hikari && !loading">
                    <p class="obs-empty">Pool metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="hikari">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="poolUtilizationPercent()">
                            <span class="obs-gauge-value">{{ poolUtilizationPercent() | number:'1.1-1' }}%</span>
                        </div>
                        <p class="obs-gauge-caption">{{ hikari.poolName }}</p>
                        <p class="obs-gauge-detail">Active {{ hikari.activeConnections }} / Max {{ hikari.maximumPoolSize }} · Idle {{ hikari.idleConnections }}</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(hikari.poolHealth)">{{ hikari.poolHealth }}</span>
                        <span class="obs-hint" *ngIf="hikari.healthDetails.isPoolExhausted">Threads awaiting connection</span>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- DB Pending - stat card -->
        <section class="obs-card obs-card-gauge obs-card-db-pending">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-database"></i>
                    DB pending
                </h2>
                <ng-container *ngIf="!hikari && !loading">
                    <p class="obs-empty">Pool metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="hikari">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="dbPendingCount() > 0 ? 100 : 0">
                            <span class="obs-gauge-value">{{ dbPendingCount() }}</span>
                        </div>
                        <p class="obs-gauge-caption">Threads awaiting connection</p>
                        <p class="obs-gauge-detail">0 is ideal · &gt; 0 indicates pressure</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + (dbPendingCount() === 0 ? 'success' : 'warning')">
                            {{ dbPendingCount() === 0 ? 'HEALTHY' : 'WARNING' }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>
        <!-- CPU Utilization - gauge card -->
        <section class="obs-card obs-card-gauge obs-card-cpu">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-stopwatch"></i>
                    CPU utilization
                </h2>
                <ng-container *ngIf="!cpu && !loading">
                    <p class="obs-empty">CPU metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="cpu">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-memory" [style.--value]="cpuUtilizationPercent()">
                            <span class="obs-gauge-value">
                                {{ cpuUtilizationPercent() | number:'1.1-1' }}%
                            </span>
                        </div>
                        <p class="obs-gauge-caption">
                            System: {{ cpu.systemCpuPercentage }} · Process: {{ cpu.processCpuPercentage }}
                        </p>
                        <p class="obs-gauge-detail">
                            Cores {{ cpu.availableProcessors }}
                        </p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(cpu.health.status)">
                            {{ cpu.health.status }}
                        </span>
                        <span class="obs-hint" *ngIf="cpu.health.recommendation">
                            {{ cpu.health.recommendation }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- Uptime - stat card -->
        <section class="obs-card obs-card-gauge obs-card-uptime">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-clock"></i>
                    Uptime
                </h2>
                <ng-container *ngIf="!uptime && !loading">
                    <p class="obs-empty">Uptime unavailable</p>
                </ng-container>
                <ng-container *ngIf="uptime">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-status" [style.--value]="100">
                            <span class="obs-gauge-value">{{ uptime.uptimeHuman }}</span>
                        </div>
                        <p class="obs-gauge-caption">Started {{ uptime.startTimeIso }}</p>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- 5xx count (last 5m) - stat card -->
        <section class="obs-card obs-card-gauge obs-card-errors">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-exclamation-circle"></i>
                    5xx (last 5m)
                </h2>
                <ng-container *ngIf="!error5xx && !loading">
                    <p class="obs-empty">Error metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="error5xx">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="error5xx.lastWindowCount > 0 ? 100 : 0">
                            <span class="obs-gauge-value">{{ error5xx.lastWindowCount }}</span>
                        </div>
                        <p class="obs-gauge-caption">5xx responses in last {{ error5xx.windowMinutes }} minutes</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(error5xx.status)">
                            {{ error5xx.status }}
                        </span>
                        <span class="obs-hint" *ngIf="error5xx.recommendation">
                            {{ error5xx.recommendation }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>
    </div>

    <!-- Section 2: User Management (UI / Auth) -->
    <h2 class="obs-section-heading">User management</h2>

    <div class="obs-grid obs-grid-user">
        <!-- Overall health - hero card for user management (one line, compact) -->
        <section class="obs-card obs-card-hero" [class.obs-loading]="loading && !uiHealth">
            <div class="obs-card-inner obs-hero-oneline">
                <h2 class="obs-card-title">
                    <i class="pi pi-heart-fill"></i>
                    Overall health
                </h2>
                <ng-container *ngIf="loading && !uiHealth">
                    <div class="obs-skeleton obs-skeleton-inline"></div>
                </ng-container>
                <ng-container *ngIf="uiHealth">
                    <div class="obs-hero-status obs-hero-status-inline" [class]="'obs-status-' + (uiHealth.status || 'UNKNOWN').toLowerCase()">
                        <div class="obs-gauge-ring obs-gauge-status obs-gauge-inline" [style.--value]="uiHealth.status === 'UP' ? 100 : 0">
                            <span class="obs-gauge-value">{{ uiHealth.status }}</span>
                        </div>
                    </div>
                    <div class="obs-components">
                        <ng-container *ngFor="let row of getUiComponentEntries()">
                            <span class="obs-pill" [class]="'obs-pill-' + statusSeverity(row[1].status)">{{ row[0] }}: {{ row[1].status }}</span>
                        </ng-container>
                    </div>
                    <button type="button" class="obs-refresh-btn" [disabled]="refreshingUser" (click)="refreshUserManagement()" title="Refresh">
                        <i class="pi" [class.pi-refresh]="!refreshingUser" [class.pi-spin]="refreshingUser" [class.pi-spinner]="refreshingUser"></i>
                        {{ refreshingUser ? 'Refreshing…' : 'Refresh' }}
                    </button>
                </ng-container>
            </div>
        </section>
<!-- JVM Memory - gauge card -->
<section class="obs-card obs-card-gauge obs-card-memory">
    <div class="obs-card-inner">
        <h2 class="obs-card-title">
            <i class="pi pi-database"></i>
            JVM memory
        </h2>
        <ng-container *ngIf="!uiMemory && !loading">
            <p class="obs-empty">Memory metrics unavailable</p>
        </ng-container>
        <ng-container *ngIf="uiMemory">
            <div class="obs-gauge-wrap">
                <div class="obs-gauge-ring obs-gauge-memory" [style.--value]="uiMemoryUtilizationPercent()">
                    <span class="obs-gauge-value">{{ uiMemoryUtilizationPercent() | number:'1.1-1' }}%</span>
                </div>
                <p class="obs-gauge-caption">Heap {{ uiMemory.heap.usedMB }} / {{ uiMemory.heap.maxMB }} MB</p>
            </div>
            <div class="obs-meta">
                <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(uiMemory.health.status)">{{ uiMemory.health.status }}</span>
                <span class="obs-hint" *ngIf="uiMemory.health.recommendation">{{ uiMemory.health.recommendation }}</span>
            </div>
        </ng-container>
    </div>
</section>

        <!-- Connection pool - gauge card -->
        <section class="obs-card obs-card-gauge obs-card-pool">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-server"></i>
                    Connection pool
                </h2>
                <ng-container *ngIf="!uiHikari && !loading">
                    <p class="obs-empty">Pool metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="uiHikari">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="uiPoolUtilizationPercent()">
                            <span class="obs-gauge-value">{{ uiPoolUtilizationPercent() | number:'1.1-1' }}%</span>
                        </div>
                        <p class="obs-gauge-caption">{{ uiHikari.poolName }}</p>
                        <p class="obs-gauge-detail">
                            Active {{ uiHikari.activeConnections }} / Max {{ uiHikari.maximumPoolSize }} · Idle {{ uiHikari.idleConnections }}
                        </p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(uiHikari.poolHealth)">{{ uiHikari.poolHealth }}</span>
                        <span class="obs-hint" *ngIf="uiHikari.healthDetails.isPoolExhausted">Threads awaiting connection</span>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- DB Pending - stat card -->
        <section class="obs-card obs-card-gauge obs-card-db-pending">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-database"></i>
                    DB pending
                </h2>
                <ng-container *ngIf="!uiHikari && !loading">
                    <p class="obs-empty">Pool metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="uiHikari">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="uiDbPendingCount() > 0 ? 100 : 0">
                            <span class="obs-gauge-value">{{ uiDbPendingCount() }}</span>
                        </div>
                        <p class="obs-gauge-caption">Threads awaiting connection</p>
                        <p class="obs-gauge-detail">0 is ideal · &gt; 0 indicates pressure</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + (uiDbPendingCount() === 0 ? 'success' : 'warning')">
                            {{ uiDbPendingCount() === 0 ? 'HEALTHY' : 'WARNING' }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>


        <!-- CPU Utilization - gauge card -->
        <section class="obs-card obs-card-gauge obs-card-cpu">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-stopwatch"></i>
                    CPU utilization
                </h2>
                <ng-container *ngIf="!uiCpu && !loading">
                    <p class="obs-empty">CPU metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="uiCpu">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-memory" [style.--value]="uiCpuUtilizationPercent()">
                            <span class="obs-gauge-value">
                                {{ uiCpuUtilizationPercent() | number:'1.1-1' }}%
                            </span>
                        </div>
                        <p class="obs-gauge-caption">
                            System: {{ uiCpu.systemCpuPercentage }} · Process: {{ uiCpu.processCpuPercentage }}
                        </p>
                        <p class="obs-gauge-detail">
                            Cores {{ uiCpu.availableProcessors }}
                        </p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(uiCpu.health.status)">
                            {{ uiCpu.health.status }}
                        </span>
                        <span class="obs-hint" *ngIf="uiCpu.health.recommendation">
                            {{ uiCpu.health.recommendation }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- Uptime - stat card -->
        <section class="obs-card obs-card-gauge obs-card-uptime">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-clock"></i>
                    Uptime
                </h2>
                <ng-container *ngIf="!uiUptime && !loading">
                    <p class="obs-empty">Uptime unavailable</p>
                </ng-container>
                <ng-container *ngIf="uiUptime">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-status" [style.--value]="100">
                            <span class="obs-gauge-value">{{ uiUptime.uptimeHuman }}</span>
                        </div>
                        <p class="obs-gauge-caption">Started {{ uiUptime.startTimeIso }}</p>
                    </div>
                </ng-container>
            </div>
        </section>

        <!-- 5xx count (last 5m) - stat card -->
        <section class="obs-card obs-card-gauge obs-card-errors">
            <div class="obs-card-inner">
                <h2 class="obs-card-title">
                    <i class="pi pi-exclamation-circle"></i>
                    5xx (last 5m)
                </h2>
                <ng-container *ngIf="!uiError5xx && !loading">
                    <p class="obs-empty">Error metrics unavailable</p>
                </ng-container>
                <ng-container *ngIf="uiError5xx">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-pool" [style.--value]="uiError5xx.lastWindowCount > 0 ? 100 : 0">
                            <span class="obs-gauge-value">{{ uiError5xx.lastWindowCount }}</span>
                        </div>
                        <p class="obs-gauge-caption">5xx responses in last {{ uiError5xx.windowMinutes }} minutes</p>
                    </div>
                    <div class="obs-meta">
                        <span class="obs-badge" [class]="'obs-badge-' + statusSeverity(uiError5xx.status)">
                            {{ uiError5xx.status }}
                        </span>
                        <span class="obs-hint" *ngIf="uiError5xx.recommendation">
                            {{ uiError5xx.recommendation }}
                        </span>
                    </div>
                </ng-container>
            </div>
        </section>
    </div>

    <!-- Active users: refresh + division-wise table -->
    <h2 class="obs-section-heading">Active users</h2>
    <div class="obs-active-users-section">
        <section class="obs-card obs-card-active-users-table">
            <div class="obs-card-inner">
                <div class="obs-active-users-header">
                    <h2 class="obs-card-title">
                        <i class="pi pi-users"></i>
                        Active users ({{ verifiedUsers.length }})
                    </h2>
                    <button
                        type="button"
                        class="obs-refresh-btn"
                        [disabled]="refreshingVerifiedUsers"
                        (click)="refreshVerifiedUsers()"
                        title="Refresh active users"
                    >
                        <i class="pi" [class.pi-refresh]="!refreshingVerifiedUsers" [class.pi-spin]="refreshingVerifiedUsers" [class.pi-spinner]="refreshingVerifiedUsers"></i>
                        {{ refreshingVerifiedUsers ? 'Refreshing…' : 'Refresh' }}
                    </button>
                </div>

                <ng-container *ngIf="loadingVerifiedUsers">
                    <div class="obs-gauge-wrap">
                        <div class="obs-gauge-ring obs-gauge-active-users obs-gauge-static">
                            <p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner>
                        </div>
                    </div>
                </ng-container>

                <ng-container *ngIf="!loadingVerifiedUsers && verifiedUsersError">
                    <p class="obs-empty">{{ verifiedUsersError }}</p>
                </ng-container>

                <ng-container *ngIf="!loadingVerifiedUsers && !verifiedUsersError">
                    <table class="obs-active-users-table-3col" *ngIf="verifiedUsers.length > 0; else noUsers">
                        <thead>
                            <tr>
                                <th>Germany</th>
                                <th>France</th>
                                <th>Netherlands</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <ng-container *ngIf="getUsersForDivision('100') as usersGermany">
                                        <div class="obs-active-users-count">{{ usersGermany.length }}</div>
                                        <ul class="obs-active-users-names" *ngIf="usersGermany.length > 0; else emptyGermany">
                                            <li *ngFor="let u of usersGermany.slice(0, maxUsersToShow)">{{ u.userName }}</li>
                                            <li *ngIf="usersGermany.length > maxUsersToShow">
                                                +{{ usersGermany.length - maxUsersToShow }} more
                                            </li>
                                        </ul>
                                        <ng-template #emptyGermany>
                                            <div class="obs-active-users-empty-small">-</div>
                                        </ng-template>
                                    </ng-container>
                                </td>
                                <td>
                                    <ng-container *ngIf="getUsersForDivision('300') as usersFrance">
                                        <div class="obs-active-users-count">{{ usersFrance.length }}</div>
                                        <ul class="obs-active-users-names" *ngIf="usersFrance.length > 0; else emptyFrance">
                                            <li *ngFor="let u of usersFrance.slice(0, maxUsersToShow)">{{ u.userName }}</li>
                                            <li *ngIf="usersFrance.length > maxUsersToShow">
                                                +{{ usersFrance.length - maxUsersToShow }} more
                                            </li>
                                        </ul>
                                        <ng-template #emptyFrance>
                                            <div class="obs-active-users-empty-small">-</div>
                                        </ng-template>
                                    </ng-container>
                                </td>
                                <td>
                                    <ng-container *ngIf="getUsersForDivision('400') as usersNetherlands">
                                        <div class="obs-active-users-count">{{ usersNetherlands.length }}</div>
                                        <ul class="obs-active-users-names" *ngIf="usersNetherlands.length > 0; else emptyNetherlands">
                                            <li *ngFor="let u of usersNetherlands.slice(0, maxUsersToShow)">{{ u.userName }}</li>
                                            <li *ngIf="usersNetherlands.length > maxUsersToShow">
                                                +{{ usersNetherlands.length - maxUsersToShow }} more
                                            </li>
                                        </ul>
                                        <ng-template #emptyNetherlands>
                                            <div class="obs-active-users-empty-small">-</div>
                                        </ng-template>
                                    </ng-container>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <ng-template #noUsers>
                        <p class="obs-active-users-empty">No verified users.</p>
                    </ng-template>
                </ng-container>
            </div>
        </section>
    </div>
</div>

